import type { FC } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";

import { DetailPage } from "src/components/layout";
import { Navigate, NoContextOutlet, type RouteObject } from "src/router";
import { FormErrorProvider } from "src/contexts/form-error-context";
import type { DetailPageTab } from "src/components/layout/detail-page";
import { useEntitlements } from "src/hooks/use-entitlement";
import { EntitledRoute } from "src/components/router/entitled-route";
import { useSpaceContext } from "src/components/spaces/space-context";
import { useAudienceTemplateSpaceAccessEnabled } from "src/components/spaces/audience-template-access/flag";
import { lazyNamed } from "src/utils/lazy-named";

const AudienceTemplates = lazyNamed(
  () => import("./audience-templates"),
  "AudienceTemplates",
);
const CreateAudienceTemplate = lazyNamed(
  () => import("./audience-templates/create"),
  "CreateAudienceTemplate",
);
const AudienceTemplateLoader = lazyNamed(
  () => import("./audience-templates/show"),
  "AudienceTemplateLoader",
);
const AudienceTemplate = lazyNamed(
  () => import("./audience-templates/show"),
  "AudienceTemplate",
);
const SyncTemplates = lazyNamed(
  () => import("./sync-templates"),
  "SyncTemplates",
);
const CloneAudienceTemplate = lazyNamed(
  () => import("./audience-templates/clone"),
  "CloneAudienceTemplate",
);
const CreateSyncTemplate = lazyNamed(
  () => import("src/pages/templates/sync-templates/create"),
  "CreateSyncTemplate",
);
const SyncTemplateRouter = lazyNamed(
  () => import("src/pages/templates/sync-templates/components"),
  "SyncTemplateRouter",
);
const JourneyTemplates = lazyNamed(
  () => import("./journey-templates"),
  "JourneyTemplates",
);
const CreateJourneyTemplateModal = lazyNamed(
  () =>
    import(
      "src/pages/templates/journey-templates/create-journey-template-modal"
    ),
  "CreateJourneyTemplateModal",
);

export const syncTemplatesIndexRoute = "/templates/sync-templates";
export const journeyTemplatesIndexRoute = "/templates/journey-templates";

// Builds tabs with isHidden based on feature flags, entitlements, and space access
const useTemplateTabs = (): DetailPageTab[] => {
  const { audienceTemplatesEnabled } = useFlags();
  const { data: entitlementsData } = useEntitlements(true, true);
  const { isInSpace, isPathAccessibleInSpace } = useSpaceContext();
  const audienceTemplateSpacesEnabled = useAudienceTemplateSpaceAccessEnabled();
  const journeysEnabled = entitlementsData.entitlements.journeys;

  const isTabHiddenInSpace = (path: string) =>
    isInSpace && !isPathAccessibleInSpace(`/templates/${path}`);

  return [
    {
      title: "Audience templates",
      path: "audience-templates",
      variant: "table",
      isHidden:
        !audienceTemplatesEnabled ||
        (isInSpace && !audienceTemplateSpacesEnabled) ||
        isTabHiddenInSpace("audience-templates"),
    },
    {
      title: "Journey templates",
      path: "journey-templates",
      variant: "table",
      isHidden: !journeysEnabled || isTabHiddenInSpace("journey-templates"),
    },
    {
      title: "Sync templates",
      path: "sync-templates",
      variant: "table",
      isHidden: isTabHiddenInSpace("sync-templates"),
    },
  ];
};

const TemplatesLayout: FC = () => {
  const tabs = useTemplateTabs();

  if (tabs.every((tab) => tab.isHidden)) {
    return <Navigate to="/" replace />;
  }

  return (
    <DetailPage heading="Templates" tabDepth={3} tabs={tabs}>
      <NoContextOutlet />
    </DetailPage>
  );
};

// Index redirect to first visible tab
const TemplatesIndexRedirect: FC = () => {
  const tabs = useTemplateTabs();
  const firstVisibleTab = tabs.find((tab) => !tab.isHidden);

  if (!firstVisibleTab) {
    return <Navigate to="/" replace />;
  }

  return (
    <Navigate
      to={{ pathname: firstVisibleTab.path, search: location.search }}
      replace
    />
  );
};

// Guard for audience templates routes
const AudienceTemplatesGuard: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { audienceTemplatesEnabled } = useFlags();
  const { isInSpace } = useSpaceContext();
  const audienceTemplateSpacesEnabled = useAudienceTemplateSpaceAccessEnabled();

  if (
    !audienceTemplatesEnabled ||
    (isInSpace && !audienceTemplateSpacesEnabled)
  ) {
    return <Navigate to="/templates" replace />;
  }

  return <>{children}</>;
};

export const routes: Array<RouteObject> = [
  {
    path: "templates/*",
    element: <TemplatesLayout />,
    spacesAccess: "accessible-from-limited-view",
    children: [
      {
        index: true,
        element: <TemplatesIndexRedirect />,
        spacesAccess: "accessible-from-limited-view",
      },
      {
        path: "audience-templates",
        element: (
          <AudienceTemplatesGuard>
            <AudienceTemplates />
          </AudienceTemplatesGuard>
        ),
        spacesAccess: "accessible-from-limited-view",
      },
      {
        path: "audience-templates/new",
        element: (
          <AudienceTemplatesGuard>
            <FormErrorProvider>
              <CreateAudienceTemplate />
            </FormErrorProvider>
          </AudienceTemplatesGuard>
        ),
        spacesAccess: "accessible-from-limited-view",
      },
      {
        element: (
          <AudienceTemplatesGuard>
            <AudienceTemplateLoader />
          </AudienceTemplatesGuard>
        ),
        spacesAccess: "accessible-from-limited-view",
        children: [
          {
            path: "audience-templates/:id/clone",
            element: <CloneAudienceTemplate />,
            spacesAccess: "accessible-from-limited-view",
          },
        ],
      },
      {
        path: "journey-templates",
        element: (
          <EntitledRoute entitlement="journeys" redirect="/templates">
            <JourneyTemplates />
          </EntitledRoute>
        ),
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "journey-templates/new",
        element: (
          <EntitledRoute entitlement="journeys" redirect="/templates">
            <CreateJourneyTemplateModal isOpen />
          </EntitledRoute>
        ),
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "sync-templates",
        element: <SyncTemplates />,
        spacesAccess: "accessible-from-limited-view",
      },
    ],
  },
  // Detail pages render their own shell, so they live outside the tabbed
  // TemplatesLayout (a nested route would put a page card inside its card).
  {
    element: (
      <AudienceTemplatesGuard>
        <AudienceTemplateLoader />
      </AudienceTemplatesGuard>
    ),
    spacesAccess: "accessible-from-limited-view",
    children: [
      {
        path: "templates/audience-templates/:id",
        element: (
          <FormErrorProvider>
            <AudienceTemplate />
          </FormErrorProvider>
        ),
        spacesAccess: "accessible-from-limited-view",
      },
    ],
  },
  {
    path: "templates/sync-templates/new",
    element: <CreateSyncTemplate />,
    spacesAccess: "accessible-from-limited-view",
  },
  {
    path: "templates/sync-templates/:id/*",
    element: <SyncTemplateRouter />,
    spacesAccess: "accessible-from-limited-view",
  },
];
